/**
 * Utility functions
 */

import { withUtm } from './utm.js';

/**
 * Escape HTML special characters to prevent XSS.
 */
export function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Cache of valid city slugs (cities that have pages)
let validCitySlugs = new Set();

/**
 * Load valid city slugs from API - call this on app init
 */
export async function loadValidCitySlugs() {
    try {
        const response = await fetch('/api/valid-cities');
        if (response.ok) {
            const data = await response.json();
            validCitySlugs = new Set(data.slugs || []);
            console.log(`Loaded ${validCitySlugs.size} valid city slugs`);
        }
    } catch (e) {
        console.warn('Failed to load valid city slugs:', e);
    }
}

/**
 * Check if a city slug has a valid page
 * @param {string} slug
 * @returns {boolean}
 */
function isCitySlugValid(slug) {
    return validCitySlugs.has(slug);
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 * @returns {number} Distance in kilometers
 */
export function getDistanceKm(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Get age info for freshness indicator
 * @param {string} timestamp
 * @returns {{ text: string, class: string, number: number, unit: string }}
 */
export function formatAge(timestamp) {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffMs = now - alertTime;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return { text: 'NU', class: 'fresh-now', number: 0, unit: 'sec' };
    if (diffMins < 5) return { text: `${diffMins}m`, class: 'fresh-recent', number: diffMins, unit: 'min' };
    if (diffMins < 15) return { text: `${diffMins}m`, class: 'fresh-warm', number: diffMins, unit: 'min' };
    if (diffMins < 60) return { text: `${diffMins}m`, class: 'fresh-old', number: diffMins, unit: 'min' };

    const diffHours = Math.floor(diffMins / 60);
    return { text: `${diffHours}u`, class: 'fresh-stale', number: diffHours, unit: 'uur' };
}


/**
 * Check if a string looks like a valid Dutch city name
 * @param {string} name
 * @returns {boolean}
 */
function isValidCityName(name) {
    if (!name || name.length < 2) return false;

    // Reject if starts with highway/road prefix (A1, N522, etc.)
    if (/^[ANan]\d/.test(name)) return false;

    // Reject if it's mostly numbers
    if (/^\d+/.test(name)) return false;

    // Reject common non-city patterns
    const invalid = ['onbekend', 'unknown', 'hectometerpaal', 'hmp', 'afrit', 'knooppunt'];
    if (invalid.some(p => name.toLowerCase().includes(p))) return false;

    return true;
}

/**
 * Convert city name to URL slug
 * @param {string} city
 * @returns {string} - empty string if not a valid city
 */
function cityToSlug(city) {
    if (!city) return '';

    // Check if it looks like a valid city name
    if (!isValidCityName(city)) return '';

    // Handle special Dutch city names
    const slugMap = {
        "'s-gravenhage": "den-haag",
        "'s-hertogenbosch": "den-bosch",
        "den haag": "den-haag",
        "den bosch": "den-bosch",
    };
    const lower = city.toLowerCase();
    if (slugMap[lower]) return slugMap[lower];
    // Default: lowercase, replace spaces with hyphens, remove special chars
    return lower.replace(/\s+/g, '-').replace(/['"]/g, '');
}

/**
 * Format address as HTML for popup - compact single-line format
 * "Street, [City]" where city is a link (only if city has a valid page)
 * @param {string} address
 * @returns {string}
 */
export function formatAddressHtml(address) {
    if (!address) return '';

    const parts = address.split(',').map(p => p.trim());

    if (parts.length >= 2) {
        const street = escapeHtml(parts[0]);
        const cityRaw = parts[parts.length - 1].trim();
        const cityName = escapeHtml(cityRaw);
        const citySlug = cityToSlug(cityRaw);
        // Only create link if city has a valid page
        const cityLink = (citySlug && isCitySlugValid(citySlug))
            ? `<a href="/${citySlug}" class="addr-city-link">${cityName}</a>`
            : cityName;
        return `<span class="addr-street">${street}</span>, ${cityLink}`;
    }

    // Single part - try to make it a link (only if valid)
    const citySlug = cityToSlug(address);
    const escaped = escapeHtml(address);
    if (citySlug && isCitySlugValid(citySlug)) {
        return `<a href="/${citySlug}" class="addr-city-link">${escaped}</a>`;
    }
    return escaped;
}

/**
 * Incident type to URL slug mapping — keep in sync with backend/services/registry.py
 */
const INCIDENT_TYPE_SLUGS = {
    fire: 'brand',
    medical: 'medisch',
    traffic: 'verkeer',
    violence: 'geweld',
    rescue: 'hulpverlening',
    water: 'water',
    hazmat: 'gevaarlijke-stoffen',
    major: 'grip',
    other: 'overig',
    unknown: 'melding',
};

/**
 * Generate detail URL for an alert
 * @param {object} alert - Alert object with city, incident_type, timestamp, id
 * @returns {string} Detail URL path
 */
export function getAlertDetailUrl(alert) {
    // City slug
    const city = alert.city || alert.address?.split(',').pop()?.trim();
    const citySlugVal = city ? cityToSlug(city) : 'onbekend';

    // Incident slug
    const incidentSlug = INCIDENT_TYPE_SLUGS[alert.incident_type] || 'melding';

    // Date from timestamp
    let dateStr = 'onbekend';
    if (alert.timestamp) {
        try {
            const dt = new Date(alert.timestamp);
            dateStr = dt.toISOString().split('T')[0];
        } catch (e) {
            // Keep default
        }
    }

    // ID suffix (last 6 chars)
    const idSuffix = alert.id ? alert.id.slice(-6) : '000000';

    return `/melding/${citySlugVal}/${incidentSlug}/${dateStr}/${idSuffix}`;
}

/**
 * Generate shareable URL with UTM parameters (share-button convention:
 * utm_medium=share_button, utm_campaign=melding, utm_content=<alert id>).
 * @param {object} alert - Alert object
 * @param {string} source - Share source (native, copy, whatsapp, twitter, facebook)
 * @returns {string} Full URL with UTM params
 */
export function getShareUrl(alert, source = 'copy') {
    const path = getAlertDetailUrl(alert);
    return withUtm(window.location.origin + path, source, 'share_button', 'melding', alert.id);
}

/**
 * Get share text for an alert
 * Uses generated_description from backend if available (detail pages),
 * otherwise creates a simple fallback for map carousel alerts
 * @param {object} alert - Alert object
 * @returns {string} Share text
 */
export function getShareText(alert) {
    // Use backend-generated description if available (detail pages)
    if (alert.generated_description) {
        return alert.generated_description;
    }

    // Simple fallback for map carousel alerts (no generated_description)
    const service = {
        brandweer: 'Brandweer',
        ambulance: 'Ambulance',
        politie: 'Politie',
        lifeliner: 'Traumahelikopter',
        knrm: 'KNRM',
        overig: 'Hulpdiensten',
    }[alert.service] || 'Hulpdiensten';

    const city = alert.city || '';
    const street = alert.street || '';

    if (street && city) {
        return `${service} naar ${street} in ${city}.`;
    } else if (city) {
        return `${service} in ${city}.`;
    }
    return `${service} melding.`;
}

/**
 * Get the link URL for a burst object.
 * @param {Object} burst - Burst object with scope property
 * @returns {string} URL path
 */
export function getBurstUrl(burst) {
    if (burst.detail_url) {
        return burst.detail_url;
    }
    if (burst.scope.startsWith('city:')) {
        return `/${burst.scope.split(':')[1]}`;
    }
    return '/pieken';
}

/**
 * Share an alert using Web Share API or fallback
 * @param {object} alert - Alert object
 * @returns {Promise<{success: boolean, method: string}>}
 */
export async function shareAlert(alert) {
    const text = getShareText(alert);

    // Try native Web Share API first (mobile-friendly)
    if (navigator.share) {
        try {
            const url = getShareUrl(alert, 'native');
            const shareText = `${text}\n\n${url}`;
            await navigator.share({ text: shareText });
            return { success: true, method: 'native' };
        } catch (err) {
            if (err.name === 'AbortError') {
                return { success: false, method: 'cancelled' };
            }
            console.warn('Web Share API failed:', err);
        }
    }

    // Fallback: copy text + URL to clipboard
    try {
        const url = getShareUrl(alert, 'copy');
        const shareText = `${text}\n\n${url}`;
        await navigator.clipboard.writeText(shareText);
        return { success: true, method: 'clipboard' };
    } catch (err) {
        console.error('Clipboard copy failed:', err);
        return { success: false, method: 'error' };
    }
}
